import { CloseOutlined, DownOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Dropdown, Image, Space } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { Package, Truck } from 'lucide-react';
import { useState } from 'react';
import { PiMicrosoftExcelLogoThin } from 'react-icons/pi';
import { useHistory, useLocation } from 'react-router-dom';

import IconButton from '../../../components/button/IconButton';
import PPTableLink from '../../../components/link/PPTableLink';
import OrderLatestNote from '../../../components/orderLatestNote';
import PPPageHeader from '../../../components/pageHeader/PPPageHeader';
import ProductImage from '../../../components/productImage';
import ColumnSettings from '../../../components/table/ColumnSettings';
import PPResizableTable from '../../../components/table/PPResizableTable';
import { useColumnSettings } from '../../../components/table/useColumnSettings';
import useStore from '../../../store/useStore';
import { useTableFilter } from '../../../utils/Filter';
import { getDateTime } from '../../../utils/format';
import { getIndexColumn } from '../../../utils/getIndexColumn';
import { exportToXLSX } from '../../../utils/xlsxHelper';
import DeliveryStatusBadge from '../components/DeliveryStatusBadge';
import { ADD_ORDER_TARGETS, getAddOrderTargets } from '../utils/utils';
import { printDeliverySlipPDF } from './printDeliverySlipPdf';

import './Style.less';

const routes = [
  {
    path: '/order',
    breadcrumbName: 'B2C/B2B захиалга',
  },
  {
    path: 'first',
    breadcrumbName: 'Хүргэлтийн захиалга',
  },
];

// meta-гээс ирсэн enum-ыг enumDictionary-аар нэрлэж шүүлтүүрийн сонголт болгох
// (enumDictionary дээр нэр нь байхгүй утгуудыг харуулахгүй)
const getEnumFilters = (field) =>
  (field?.enum ?? [])
    .filter((value) => field?.enumDictionary?.[value])
    .map((value) => ({
      text: field.enumDictionary[value],
      value,
    }));

// Сүүлийн тэмдэглэлийг авах: API-аас latestNote объектоор ирдэг,
// байхгүй бол orderNote массивын хамгийн сүүлийн бичлэгийг авна
const getLatestNote = (record) =>
  record?.latestNote ?? record?.orderNote?.[record.orderNote.length - 1];

const getColumns = (location, getColumnSearchProps, getColumnFilterProps, pagination, meta) => [
  getIndexColumn(pagination.current, pagination.pageSize),
  {
    title: 'Захиалгын дугаар',
    dataIndex: 'code',
    key: 'code',
    ...getColumnSearchProps('code', 'Захиалгын дугаар'),
    render: (text, record) => (
      <PPTableLink
        to={{
          pathname: `/merchant/order/deliveries/${record._id}`,
          state: {
            search: location.search,
          },
        }}
      >
        {text}
      </PPTableLink>
    ),
    width: 200,
  },
  {
    title: 'Харилцагч',
    dataIndex: 'customerName',
    key: 'customerName',
    width: 200,
    ...getColumnSearchProps('customerName', 'Харилцагч'),
  },
  {
    title: 'Утас',
    dataIndex: 'customerPhone',
    key: 'customerPhone',
    width: 200,
    ...getColumnSearchProps('customerPhone', 'Утас'),
  },
  {
    title: 'Барааны мэдээлэл',
    key: 'productImages',
    width: 200,
    render: (_, record) =>
      record?.childOrders?.length ? (
        <Image.PreviewGroup>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {record.childOrders.slice(0, 3).map((child, index) => (
              <ProductImage
                key={index}
                src={child.productImage}
                size={40}
                preview
                style={{
                  objectFit: 'cover',
                  borderRadius: 6,
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              />
            ))}
            {record.childOrders.length > 3 && (
              <div
                style={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  background: '#f0f0f0',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#595959',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                +{record.childOrders.length - 3}
              </div>
            )}
          </div>
        </Image.PreviewGroup>
      ) : (
        '-'
      ),
  },
  {
    title: 'Баримт',
    dataIndex: 'documents',
    key: 'documents',
    width: 200,
    render: (documents) =>
      documents?.length ? (
        <Image.PreviewGroup>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {documents.slice(0, 3).map((document, index) => (
              <Image
                key={index}
                src={document}
                width={40}
                height={40}
                style={{
                  objectFit: 'cover',
                  borderRadius: 6,
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              />
            ))}
            {documents.length > 3 && (
              <div
                style={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  background: '#f0f0f0',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#595959',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                +{documents.length - 3}
              </div>
            )}
          </div>
        </Image.PreviewGroup>
      ) : (
        '-'
      ),
  },
  {
    title: 'Төлөв',
    dataIndex: 'orderStatus',
    key: 'orderStatus',
    width: 300,
    filters: getEnumFilters(meta?.orderStatus),
    ...getColumnFilterProps('orderStatus'),
    render: (orderStatus, record) =>
      orderStatus ? (
        <div className="delivery-status-cell">
          <DeliveryStatusBadge
            status={orderStatus}
            label={meta?.orderStatus?.enumDictionary?.[orderStatus]}
          />
          {record?.orderStatusDate ? (
            <span className="delivery-status-date">
              {dayjs(record.orderStatusDate).format('YYYY-MM-DD HH:mm:ss')}
            </span>
          ) : (
            '-'
          )}
        </div>
      ) : (
        '-'
      ),
  },
  {
    title: 'Нийт захиалгын дүн',
    dataIndex: 'totalPrice',
    key: 'totalPrice',
    width: 200,
    render: (value) => `${(value ?? 0).toLocaleString('mn-MN')} ₮`,
  },
  {
    title: 'Хүргэлтийн төлбөр',
    dataIndex: 'totalServiceAmount',
    key: 'totalServiceAmount',
    width: 200,
    render: (value) => `${(value ?? 0).toLocaleString('mn-MN')} ₮`,
  },
  {
    title: 'Хүргэлтийн төрөл',
    dataIndex: 'requestType',
    key: 'requestType',
    width: 200,
    filters: getEnumFilters(meta?.requestType),
    ...getColumnFilterProps('requestType'),
    render: (requestType) =>
      requestType ? (
        <span className="delivery-type-badge">
          <span className="delivery-status-dot" />
          {meta?.requestType?.enumDictionary?.[requestType] ?? requestType}
        </span>
      ) : (
        '-'
      ),
  },
  {
    title: 'Сүүлийн тэмдэглэл',
    dataIndex: 'latestNote',
    key: 'latestNote',
    width: 200,
    // Нүдэнд таслаад харуулж, hover хийхэд бүтэн тэмдэглэл popover-оор гарна
    render: (_, record) => <OrderLatestNote note={getLatestNote(record)} />,
  },
  {
    title: 'Гаднаас үүссэн захиалга',
    dataIndex: 'fromThirdPartyCode',
    key: 'fromThirdPartyCode',
    width: 200,
    alignItems: 'center',
    render: (fromThirdPartyCode) => {
      const value = String(fromThirdPartyCode);
      if (value !== 'true' && value !== 'false') return '-';

      const isThirdParty = value === 'true';
      return (
        <span
          className={`third-party-badge ${isThirdParty ? 'third-party-yes' : 'third-party-no'}`}
        >
          <span className="delivery-status-dot" />
          {isThirdParty ? 'Тийм' : 'Үгүй'}
        </span>
      );
    },
    ...getColumnSearchProps('fromThirdPartyCode', 'Гаднаас үүссэн захиалга'),
  },
];

const List = () => {
  const location = useLocation();
  const history = useHistory();
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [printingSlips, setPrintingSlips] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  /**
   * Мерчантын гэрээний төрөл — нэвтрэх/merchant солих үед серверээс ирж
   * store-д хадгалагддаг (`"WO"`, `"WOD"` мэт үсгүүдийн мөр). Аппын бусад
   * дэлгэц ч гэрээгээ эндээс шалгадаг тул нэмэлт хүсэлт хийхгүй.
   */
  const contractType = useStore((state) => state.user.contractType);

  const fetchData = async ({ page = 1, pageSize = 10, ...filters } = {}) => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/v1/request/list-delivery', {
        page,
        pageSize,
        ...filters,
      });
      setData(data?.result?.docs ?? []);
      setMeta(data?.result?.meta ?? {});
      setPagination({
        current: data?.result?.page ?? page,
        pageSize: data?.result?.limit ?? pageSize,
        total: data?.result?.totalDocs ?? 0,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const { getColumnSearchProps, getColumnFilterProps, handleTableChange, withHighlight } =
    useTableFilter({
      fetchData,
    });

  const { columns, settingsProps, onColumnOrderChange } = useColumnSettings({
    columns: withHighlight(
      getColumns(location, getColumnSearchProps, getColumnFilterProps, pagination, meta)
    ),
    storageKey: 'orderDelivery.list',
  });

  const isRowSelected = Boolean(selectedRowKeys.length);
  const closeRowSelection = () => setSelectedRowKeys([]);

  // Сонгосон захиалгуудын хүргэлтийн хуудсыг хэвлэх
  const printDeliverySlips = async () => {
    setPrintingSlips(true);
    try {
      const { data } = await axios.post('/api/v1.0/request/delivery-slip', {
        orderIds: selectedRowKeys,
      });
      if (data.statusCode === 200) {
        const slips = data.result?.filter((slip) => slip.origin !== 'mcscc');
        if (slips.length > 0) printDeliverySlipPDF(slips);
      }
    } finally {
      setPrintingSlips(false);
    }
  };

  // Сонгосон захиалгуудыг Excel руу татах
  const exportSelectedOrders = () => {
    const selectOrders = data.filter((doc) => selectedRowKeys.includes(doc._id));
    const excelData = selectOrders.map((o) => {
      const productBarCodes = o?.childOrders?.reduce((group, item) => {
        group.push(...(item?.productBarCodes || []));
        return group;
      }, []);
      return {
        ...o,
        createdAt: getDateTime(o?.createdAt),
        productBarCodes: productBarCodes?.join(','),
        orderNote: getLatestNote(o)?.note ?? '',
        orderStatus: meta?.orderStatus?.enumDictionary?.[o?.orderStatus] ?? o?.orderStatus,
        requestType: meta?.requestType?.enumDictionary?.[o?.requestType] ?? o?.requestType,
      };
    });

    exportToXLSX({
      data: excelData,
      columns: [
        'createdAt',
        'code',
        'customerName',
        'customerPhone',
        'productBarCodes',
        'orderNote',
        'orderStatus',
        'requestType',
      ],
      columnNames: [
        'Захиалга үүссэн',
        'Захиалгын дугаар',
        'Харилцагч',
        'Утасны дугаар',
        'Барааны баркодууд',
        'Сүүлийн тэмдэглэл',
        'Захиалгын төлөв',
        'Хүргэлтийн төрөл',
      ],
      fileName: `Захиалгын тайлан`,
    });
  };

  // Мөр сонгогдсон үед page header дээр гарч ирэх товчнууд
  const rowSelectedTitle = (
    <Space>
      <IconButton icon={<PrinterOutlined />} onClick={printDeliverySlips} loading={printingSlips} />
      <IconButton icon={<PiMicrosoftExcelLogoThin size="17px" />} onClick={exportSelectedOrders} />
      <div className="pp_ph_sel_items_count">{`${selectedRowKeys.length} Сонгогдсон`}</div>
    </Space>
  );

  const rowSelectedExtraActions = (
    <Button size="large" type="text" onClick={closeRowSelection}>
      <CloseOutlined />
    </Button>
  );

  /**
   * Шинэ захиалга үүсгэх. Гэрээний төрлөөс хамаарна: шууд хүргэлтийн гэрээтэй
   * бол шууд хүргэлтийн хуудас руу, хоёулаа гэрээтэй бол сонголт болж задарна.
   */
  const addOrderTargets = getAddOrderTargets(contractType);

  const targetIcons = {
    [ADD_ORDER_TARGETS.delivery.key]: <Package size={16} strokeWidth={1.8} />,
    [ADD_ORDER_TARGETS.directDelivery.key]: <Truck size={16} strokeWidth={1.8} />,
  };

  const addOrderAction =
    addOrderTargets.length > 1 ? (
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        menu={{
          items: addOrderTargets.map((target) => ({
            key: target.key,
            label: target.label,
            icon: targetIcons[target.key],
            onClick: () => history.push(target.path),
          })),
        }}
      >
        <Button type="primary" icon={<PlusOutlined />}>
          <Space size={6}>
            Шинэ захиалга
            <DownOutlined style={{ fontSize: 10 }} />
          </Space>
        </Button>
      </Dropdown>
    ) : (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => history.push(addOrderTargets[0].path)}
      >
        Шинэ захиалга
      </Button>
    );

  return (
    <>
      <PPPageHeader
        title={isRowSelected ? rowSelectedTitle : 'Хүргэлтийн захиалга'}
        breadcrumb={{ routes }}
        extra={
          isRowSelected ? (
            rowSelectedExtraActions
          ) : (
            <Space>
              <ColumnSettings {...settingsProps} />
              {addOrderAction}
            </Space>
          )
        }
      />
      <div className="pp_table_wrapper">
        <PPResizableTable
          dataSource={data}
          columns={columns}
          onColumnOrderChange={onColumnOrderChange}
          size="small"
          rowKey="_id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Нийт ${total} илэрц`,
          }}
          scroll={{ x: 2000 }}
        />
      </div>
    </>
  );
};

export default List;
